import { uuidv7 } from "uuidv7"

import { GlobalComposable } from "~/classes/composable/GlobalComposable"
import { StaticConversation } from "~/classes/conversation/StaticConversation"
import { PropertySchema } from "~/classes/entity"
import { StaticFile } from "~/classes/file/StaticFile"
import { InterfaceSidebarState } from "~/classes/generated/interface"
import { sendSelfSidebarRequest } from "~/classes/handlers/user/sidebar"
import { sendSelfUpdateRequests } from "~/classes/handlers/user/update"
import { StaticSearch } from "~/classes/search/StaticSearch"
import { InterfaceUser } from "~/classes/users/InterfaceUser"
import { defaultUserInterfaceSettings } from "~/utils/flatbuffers/user"

class Sidebar extends GlobalComposable<{
  favoriteFiles: Record<string, StaticFile>
  favoriteSearches: Record<string, StaticSearch>
  favoriteConversations: Record<string, StaticConversation>

  width: number
  state: InterfaceSidebarState
}> {
  override defaultState = () => ({
    favoriteFiles: {},
    favoriteSearches: {},
    favoriteConversations: {},
    entries: {},
    width: defaultUserInterfaceSettings().sidebarWidth,
    state: InterfaceSidebarState.Favorites,
  })

  override onCreate = () => {
    const { userInterfaceSettings } = useSelf()
    const { addInterfaceEventListener } = useEvents()

    addInterfaceEventListener("selfSidebarResponse", this.onSelfSidebarResponse)
    addInterfaceEventListener("didConnectAuthStatus", this.onDidConnect)
    addInterfaceEventListener("didDisconnectAuthStatus", this.onDidDisconnect)
    addInterfaceEventListener("wentOnlineAuthStatus", this.onWentOnline)

    watchImmediate(userInterfaceSettings, (settings) => {
      this.state.value.state = settings.sidebarState
      this.state.value.width = settings.sidebarWidth
    })
  }

  override onDestroy = () => {
    const { removeInterfaceEventListener } = useEvents()
    this.clearSidebarEntriesAndEventHandlers()
    removeInterfaceEventListener("selfSidebarResponse", this.onSelfSidebarResponse)
    removeInterfaceEventListener("didConnectAuthStatus", this.onDidConnect)
    removeInterfaceEventListener("didDisconnectAuthStatus", this.onDidDisconnect)
    removeInterfaceEventListener("wentOnlineAuthStatus", this.onWentOnline)
  }

  clearSidebarEntriesAndEventHandlers = () => {
    for (const file of Object.values(this.state.value.favoriteFiles))
      this.onSidebarFileRemoved(file.id)
    this.state.value.favoriteFiles = {}

    for (const search of Object.values(this.state.value.favoriteSearches))
      this.onSidebarSearchRemoved(search.id)
    this.state.value.favoriteSearches = {}

    for (const conversation of Object.values(this.state.value.favoriteConversations))
      this.onSidebarConversationRemoved(conversation.id)
    this.state.value.favoriteConversations = {}
  }

  /** Sidebar favorite files organized by file id */
  sidebarFavoriteFiles = computed(
    () => dictByKey(Object.values(this.state.value.favoriteFiles), f => f.id),
  )

  sidebarFavoriteSearches = computed(
    () => dictByKey(Object.values(this.state.value.favoriteSearches), s => s.id),
  )

  sidebarFavoriteConversations = computed(
    () => dictByKey(Object.values(this.state.value.favoriteConversations), c => c.id),
  )

  onDidConnect = (datagram: DidConnectAuthStatusDatagram) => {
    this.debug("User connected, setting up user properties listener")
    const { addEntityEventListener } = useEvents()
    addEntityEventListener("userProperties", datagram.userId, this.onUserProperties)
  }

  onDidDisconnect = (datagram: DidDisconnectAuthStatusDatagram) => {
    this.debug("User disconnected, removing user properties listener")
    const { removeEntityEventListener } = useEvents()
    removeEntityEventListener("userProperties", datagram.userId, this.onUserProperties)
  }

  onWentOnline = () => {
    this.debug("User went online, requesting sidebar items")
    this.requestSelfSidebar()
  }

  showSidebar = () => {
    this.state.value.state = InterfaceSidebarState.Favorites
  }

  hideSidebar = () => {
    this.state.value.state = InterfaceSidebarState.Hidden
  }

  /**
   * Currently, anonymous users do not see a sidebar
   */
  isSidebarVisible = () => {
    const { isAuthenticated } = useAuthentication()
    if (!isAuthenticated())
      return false
   return this.state.value.state !== InterfaceSidebarState.Hidden
  }

  persistSidebar = () => {
    const { persistUserInterfaceSettings } = useSelf()
    const { isAuthenticated } = useAuthentication()

    if (isAuthenticated())
      persistUserInterfaceSettings({ sidebarState: this.state.value.state })
  }

  showSidebarAndPersist = () => {
    this.showSidebar()
    this.persistSidebar()
  }

  hideSidebarAndPersist = () => {
    this.hideSidebar()
    this.persistSidebar()
  }

  requestSelfSidebar = () => {
    const request: SelfSidebarRequestDatagram = {
      requestId: generateWorkerRequestId(),
      offline: false,
    }
    sendSelfSidebarRequest(request)
    return request
  }

  onSelfSidebarResponse = (response: SelfSidebarResponseDatagram) => {
    const { cacheState } = useCache()

    if (response.error || !response.sidebarItems)
      throw new Error("Requesting sidebar items failed")

    if (!cacheState.value.userId) {
      this.error("No user id in cache state, cannot set up sidebar event listeners", cacheState.value.userId, cacheState.value.anonId, cacheState.value.status)
      throw new Error("No user id in cache state")
    }

    for (const entry of Object.values(response.sidebarItems)) {
      if (entry.fileItem) {
        const file = new StaticFile(entry.fileItem)
        this.onSidebarFileAdded(file, entry.fractionalIndex)
      }
      else if (entry.searchItem) {
        const search = new StaticSearch(entry.searchItem)
        this.onSidebarSearchAdded(search, entry.fractionalIndex)
      }
      else if (entry.conversationItem) {
        const conversation = new StaticConversation(entry.conversationItem)
        this.onSidebarConversationAdded(conversation, entry.fractionalIndex)
      }
      else
        this.error("Unknown sidebar item type", entry)
    }
  }

  onUserProperties = (event: UserEvent) => {
    const iUser = InterfaceUser.fromEvent(event)
    const properties = iUser.sidebarEntryProperties()
    for (const property of properties) {
      if (property.value.type !== PropertySchema.UserSidebarItemIdValue)
        throw new Error("Not a sidebar property")
      const entry = property.value.UserSidebarItemId

      if (entry.fileItemId) {
        if (property.deleted)
          return this.onSidebarFileRemoved(entry.fileItemId)
        else if (this.state.value.favoriteFiles[entry.fractionalIndex])
          continue // File already exists in sidebar
        const file = StaticFile.blank(entry.fileItemId)
        this.onSidebarFileAdded(file, entry.fractionalIndex)
      }

      else if (entry.searchItemId) {
        if (property.deleted)
          return this.onSidebarSearchRemoved(entry.searchItemId)
        else if (this.state.value.favoriteSearches[entry.fractionalIndex])
          continue // Search already exists in sidebar
        const search = StaticSearch.blank(entry.searchItemId)
        this.onSidebarSearchAdded(search, entry.fractionalIndex)
      }

      else if (entry.conversationItemId) {
        if (property.deleted)
          return this.onSidebarConversationRemoved(entry.conversationItemId)
        else if (this.state.value.favoriteConversations[entry.fractionalIndex])
          continue // Conversation already exists in sidebar
        const conversation = StaticConversation.blank(entry.conversationItemId)
        this.onSidebarConversationAdded(conversation, entry.fractionalIndex)
      }
    }
  }

  /**
   * Adds a set of files to the sidebar by constructing fractional indexes for the files
   * between the boundaries specified.
   *
   * TODO: randomize the key generation somewhat to prevent two users from generating identical keys
   */
  addFilesToSidebar = async (
    files: Record<string, StaticFile>,
    [low, high]: [string | undefined, string | undefined] = [undefined, undefined],
  ) => {
    const { state: myself } = useSelf()
    const userId = myself.value.user?.id
    if (!isDefined(userId))
      throw new Error("Only users can add to sidebar")

    const timestamp = uuidv7()
    const indices = generateIndicesBetween(len(files), low, high)
    const properties: Record<string, UserProperty> = {}

    // remove any files that were there before (should we allow duplicates?)
    const fileIds = new Set(Object.values(files).map(f => f.id))
    for (const [fractionalIndex, file] of Object.entries(this.state.value.favoriteFiles)) {
      if (!file?.id || !fileIds.has(file.id))
        continue
      const property = await InterfaceUser.createPropertyForSidebarFile({
        userId,
        fileId: file.id,
        fractionalIndex,
        timestamp,
      })
      property.deleted = true
      properties[property.propertyId] = property
    }

    for (const [file, fractionalIndex] of zip(Object.values(files), indices)) {
      this.sidebarFavoriteFiles.value[fractionalIndex] = file
      const property = await InterfaceUser.createPropertyForSidebarFile({
        userId,
        fileId: file.id,
        fractionalIndex,
        timestamp,
      })
      properties[property.propertyId] = property
    }

    // TODO: make the user update endpoint return a dict of errors/successes

    const updates: SelfUpdateRequestDatagram[] = Object
      .values(properties)
      .map((property) => {
        const user = InterfaceUser.blank(userId)
        user.addProperty(property)
        return {
          user: user.inner(),
          requestId: generateWorkerRequestId(),
          offline: false,
        }
      })

    if (len(updates) > 0)
      sendSelfUpdateRequests(updates)
  }

  removeFilesFromSidebar = async (
    files: Record<string, StaticFile>,
  ) => {
    const { state: myself } = useSelf()
    const userId = myself.value.user?.id
    if (!isDefined(userId))
      throw new Error("Only users can add to sidebar")

    const timestamp = uuidv7()
    const properties: Record<string, UserProperty> = {}

    // remove any files that were there before (should we allow duplicates?)
    const fileIds = new Set(Object.values(files).map(f => f.id))
    for (const [fractionalIndex, file] of Object.entries(this.state.value.favoriteFiles)) {
      if (!file?.id || !fileIds.has(file.id))
        continue
      const property = await InterfaceUser.createPropertyForSidebarFile({
        userId,
        fileId: file.id,
        fractionalIndex,
        timestamp,
      })
      property.deleted = true
      properties[property.propertyId] = property
    }

    // TODO: make the user update endpoint return a dict of errors/successes

    const updates: SelfUpdateRequestDatagram[] = Object
      .values(properties)
      .map((property) => {
        const user = InterfaceUser.blank(userId)
        user.addProperty(property)
        return {
          user: user.inner(),
          requestId: generateWorkerRequestId(),
          offline: false,
        }
      })
    if (len(updates) > 0)
      sendSelfUpdateRequests(updates)
  }

  onSidebarFileAdded = (file: StaticFile, fractionalIndex: string) => {
    const { addEntityEventListener } = useEvents()
    if (this.state.value.favoriteFiles[fractionalIndex])
      return

    this.state.value.favoriteFiles[fractionalIndex] = file
    // don't need to have file property listener on this as the component will
    // keep the file up to date.
    addEntityEventListener("fileDeleted", file.id, this.onSidebarFileDeleted)
  }

  onSidebarFileDeleted = (event: FileEvent) => {
    const iFile = StaticFile.fromEvent(event)
    this.onSidebarFileRemoved(iFile.id)
  }

  onSidebarFileRemoved = (fileId: string) => {
    const { removeEntityEventListener } = useEvents()

    // find if the file id is in the favorite files
    const result = Object.entries(this.state.value.favoriteFiles)
      .find(([_, f]) => f.id === fileId)
    if (!result)
      return this.error("Can't remove sidebar file, already removed")

    const [fractionalIndex, _] = result
    delete this.state.value.favoriteFiles[fractionalIndex]

    removeEntityEventListener("fileDeleted", fileId, this.onSidebarFileDeleted)
  }

  /**
   * Adds a set of conversations to the sidebar by constructing fractional indexes for the conversations
   * between the boundaries specified.
   *
   * TODO: randomize the key generation somewhat to prevent two users from generating identical keys
   */
  addConversationsToSidebar = async (
    conversations: Record<string, StaticConversation>,
    [low, high]: [string | undefined, string | undefined] = [undefined, undefined],
  ) => {
    const { state: myself } = useSelf()
    const userId = myself.value.user?.id
    if (!isDefined(userId))
      throw new Error("Only users can add to sidebar")

    const timestamp = uuidv7()
    const indices = generateIndicesBetween(len(conversations), low, high)
    const properties: Record<string, UserProperty> = {}

    // remove any conversations that were there before (should we allow duplicates?)
    const conversationIds = new Set(Object.values(conversations).map(f => f.id))
    for (const [fractionalIndex, conversation] of Object.entries(this.state.value.favoriteConversations)) {
      if (!conversation?.id || !conversationIds.has(conversation.id))
        continue
      const property = await InterfaceUser.createPropertyForSidebarConversation({ userId, conversationId: conversation.id, fractionalIndex, timestamp })
      property.deleted = true
      properties[property.propertyId] = property
    }

    for (const [conversation, fractionalIndex] of zip(Object.values(conversations), indices)) {
      this.sidebarFavoriteConversations.value[fractionalIndex] = conversation
      const property = await InterfaceUser.createPropertyForSidebarConversation({
        userId,
        conversationId: conversation.id,
        fractionalIndex,
        timestamp,
      })
      properties[property.propertyId] = property
    }

    // TODO: make the user update endpoint return a dict of errors/successes

    const updates: SelfUpdateRequestDatagram[] = Object
      .values(properties)
      .map((property) => {
        const user = InterfaceUser.blank(userId)
        user.addProperty(property)
        return {
          user: user.inner(),
          requestId: generateWorkerRequestId(),
          offline: false,
        }
      })

    if (len(updates) > 0)
      sendSelfUpdateRequests(updates)
  }

  removeConversationsFromSidebar = async (
    conversations: Record<string, StaticConversation>,
  ) => {
    const { state: myself } = useSelf()
    const userId = myself.value.user?.id
    if (!isDefined(userId))
      throw new Error("Only users can add to sidebar")

    const timestamp = uuidv7()
    const properties: Record<string, UserProperty> = {}

    // remove any conversations that were there before (should we allow duplicates?)
    const conversationIds = new Set(Object.values(conversations).map(f => f.id))
    for (const [fractionalIndex, conversation] of Object.entries(this.state.value.favoriteConversations)) {
      if (!conversation?.id || !conversationIds.has(conversation.id))
        continue
      const property = await InterfaceUser.createPropertyForSidebarConversation({
        userId,
        conversationId: conversation.id,
        fractionalIndex,
        timestamp,
      })
      property.deleted = true
      properties[property.propertyId] = property
    }

    // TODO: make the user update endpoint return a dict of errors/successes

    const updates: SelfUpdateRequestDatagram[] = Object
      .values(properties)
      .map((property) => {
        const user = InterfaceUser.blank(userId)
        user.addProperty(property)
        return {
          user: user.inner(),
          requestId: generateWorkerRequestId(),
          offline: false,
        }
      })
    if (len(updates) > 0)
      sendSelfUpdateRequests(updates)
  }

  onSidebarConversationAdded = (
    conversation: StaticConversation,
    fractionalIndex: string,
  ) => {
    if (this.state.value.favoriteConversations[fractionalIndex])
      return
    // No need for any additional listeners here.
    this.state.value.favoriteConversations[fractionalIndex] = conversation
  }

  onSidebarConversationRemoved = (conversationId: string) => {
    const result = Object.entries(this.state.value.favoriteConversations)
      .find(([_, f]) => f.id === conversationId)
    if (!result)
      return this.error("Can't remove sidebar file, already removed")

    const [fractionalIndex, _] = result
    delete this.state.value.favoriteConversations[fractionalIndex]
  }

  /**
   * Adds a set of searches to the sidebar by constructing fractional indexes for the searches
   * between the boundaries specified.
   *
   * TODO: randomize the key generation somewhat to prevent two users from generating identical keys
   */
  addSearchesToSidebar = async (
    searches: Record<string, StaticSearch>,
    [low, high]: [string | undefined, string | undefined] = [undefined, undefined],
  ) => {
    const { state: myself } = useSelf()
    const userId = myself.value.user?.id
    if (!isDefined(userId))
      throw new Error("Only users can add to sidebar")

    const timestamp = uuidv7()
    const indices = generateIndicesBetween(len(searches), low, high)
    const properties: Record<string, UserProperty> = {}

    // remove any searches that were there before (should we allow duplicates?)
    const searchIds = new Set(Object.values(searches).map(f => f.id))
    for (const [fractionalIndex, search] of Object.entries(this.state.value.favoriteSearches)) {
      if (!search?.id || !searchIds.has(search.id))
        continue
      const property = await InterfaceUser.createPropertyForSidebarSearch({ userId, searchId: search.id, fractionalIndex, timestamp })
      property.deleted = true
      properties[property.propertyId] = property
    }

    for (const [search, fractionalIndex] of zip(Object.values(searches), indices)) {
      this.sidebarFavoriteSearches.value[fractionalIndex] = search
      const property = await InterfaceUser.createPropertyForSidebarSearch({
        userId,
        searchId: search.id,
        fractionalIndex,
        timestamp,
      })
      properties[property.propertyId] = property
    }

    // TODO: make the user update endpoint return a dict of errors/successes

    const updates: SelfUpdateRequestDatagram[] = Object
      .values(properties)
      .map((property) => {
        const user = InterfaceUser.blank(userId)
        user.addProperty(property)
        return {
          user: user.inner(),
          requestId: generateWorkerRequestId(),
          offline: false,
        }
      })

    if (len(updates) > 0)
      sendSelfUpdateRequests(updates)
  }

  removeSearchesFromSidebar = async (
    searches: Record<string, StaticSearch>,
  ) => {
    const { state: myself } = useSelf()
    const userId = myself.value.user?.id
    if (!isDefined(userId))
      throw new Error("Only users can add to sidebar")

    const timestamp = uuidv7()
    const properties: Record<string, UserProperty> = {}

    // remove any searches that were there before (should we allow duplicates?)
    const searchIds = new Set(Object.values(searches).map(f => f.id))
    for (const [fractionalIndex, search] of Object.entries(this.state.value.favoriteSearches)) {
      if (!search?.id || !searchIds.has(search.id))
        continue
      const property = await InterfaceUser.createPropertyForSidebarSearch({
        userId,
        searchId: search.id,
        fractionalIndex,
        timestamp,
      })
      property.deleted = true
      properties[property.propertyId] = property
    }

    // TODO: make the user update endpoint return a dict of errors/successes

    const updates: SelfUpdateRequestDatagram[] = Object
      .values(properties)
      .map((property) => {
        const user = InterfaceUser.blank(userId)
        user.addProperty(property)
        return {
          user: user.inner(),
          requestId: generateWorkerRequestId(),
          offline: false,
        }
      })
    if (len(updates) > 0)
      sendSelfUpdateRequests(updates)
  }

  onSidebarSearchAdded = (search: StaticSearch, fractionalIndex: string) => {
    if (this.state.value.favoriteSearches[fractionalIndex])
      return
    // No need for any additional listeners here.
    this.state.value.favoriteSearches[fractionalIndex] = search
  }

  onSidebarSearchRemoved = (searchId: string) => {
    const result = Object.entries(this.state.value.favoriteSearches)
      .find(([_, f]) => f.id === searchId)
    if (!result)
      return this.error("Can't remove sidebar file, already removed")

    const [fractionalIndex, _] = result
    delete this.state.value.favoriteSearches[fractionalIndex]
  }
}

const sidebar = new Sidebar("sidebar", theme.colors.blue.hex)
export default function () {
  return sidebar.use()
}
